import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTicket, markTicketRead } from '@/services/api';
import { associateTicketWithClient, updateTicketStatus } from '@/services/ticketsApi';
import { updateAccount } from '@/services/accountsApi';
import { useClientMutation } from '@/features/clients/hooks/useClientMutation';
import { TICKET_PRIORITY_LABELS, TICKET_CATEGORY_LABELS } from '@/types';
import type { TicketPriority, TicketCategory } from '@/types';
import type { CreateClientFormData } from '@/components/forms/CreateClientForm';
import type { EditClientFormData } from '@/components/forms/EditClientForm';
import {
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  IdentificationIcon,
  PencilSquareIcon,
  TagIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import ClientInfoBox from '@/components/relationships/ClientInfoBox';
import InternalNotesPanel from './InternalNotesPanel';
import AssignOwner from './AssignOwner';
import TicketReplyComposer from './components/TicketReplyComposer';
import TicketRepliesThread from './components/TicketRepliesThread';
import StatusDropdown, { getTicketStatusColor, formatStatusLabel } from '@/components/shared/StatusDropdown';

function getTicketPriorityPillClass(priority: TicketPriority): string {
  if (priority === 'High' || priority === 'Urgent') {
    return 'bg-red-100 text-red-700';
  }
  if (priority === 'Normal') {
    return 'bg-yellow-100 text-yellow-700';
  }
  return 'bg-green-100 text-green-700';
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  // Bug 1/2 (Feature 039): snapshot of the user's last-read time captured before mark-read overwrites it.
  // undefined = request not yet returned; null = never read before; string = ISO timestamp.
  const [lastReadAt, setLastReadAt] = useState<string | null | undefined>(undefined);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => getTicket(id!),
    enabled: !!id,
  });

  // US2 (Feature 039): Mark ticket as read on mount; capture previousLastReadAt for thread highlights.
  // Surgically flip isUnread=false in the cached list instead of invalidating the full list —
  // this prevents customerResponded from being re-evaluated until the SDR actually sends a reply.
  useEffect(() => {
    if (!id) return;
    markTicketRead(id).then(({ previousLastReadAt }) => {
      setLastReadAt(previousLastReadAt);
      qc.setQueriesData<{ items: any[]; total: number }>(
        { queryKey: ['tickets'] },
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((t) =>
              t.ticketId === id ? { ...t, isUnread: false } : t
            ),
          };
        }
      );
    });
  }, [id, qc]);

  const transition = useMutation({
    mutationFn: async (newStatus: string) => {
      return updateTicketStatus(id!, newStatus);
    },
    onMutate: async (newStatus) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ['ticket', id] });

      // Snapshot previous value
      const previousTicket = qc.getQueryData(['ticket', id]);

      // Optimistically update to new value
      qc.setQueryData(['ticket', id], (old: any) => ({
        ...old,
        status: newStatus,
      }));

      return { previousTicket };
    },
    onError: (_err, _newStatus, context) => {
      // Revert on error
      qc.setQueryData(['ticket', id], context?.previousTicket);
      alert('Failed to update status. Please try again.');
    },
    onSuccess: () => {
      // Invalidate queries to refetch fresh data
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
  });

  // Feature 018 US2: Client creation mutation
  const createClientMutation = useClientMutation();

  // Feature 018: Client association mutation with optimistic updates
  const associateMutation = useMutation({
    mutationFn: ({ clientId, etag }: { clientId: string; etag?: string }) =>
      associateTicketWithClient(id!, clientId, etag),
    onSuccess: () => {
      // Invalidate and refetch ticket data to get updated clientInfo
      qc.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to associate account. Please try again.');
    },
  });

  // Feature 018: Unassociate client mutation
  const unassociateMutation = useMutation({
    mutationFn: ({ etag }: { etag?: string }) =>
      associateTicketWithClient(id!, null, etag),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to remove account association. Please try again.');
    },
  });

  const handleAssociate = async (clientId: string) => {
    await associateMutation.mutateAsync({ clientId });
  };

  const handleCreate = async (data: CreateClientFormData) => {
    const result = await createClientMutation.mutateAsync({
      companyName: data.companyName,
      firstName: data.firstName,
      lastName: data.lastName,
      contactName: `${data.firstName} ${data.lastName}`, // Auto-generate contact name
      email: data.email,
      phone: data.phone,
      country: data.country,
      city: data.city || undefined,
      street: data.street || undefined,
      clientSegment: data.clientSegment, // Feature 019: Include client segment
    });

    return {
      clientId: result.account.accountId,
      warning: result.warning,
    };
  };

  const handleEdit = async (clientId: string, data: EditClientFormData) => {
    // Map frontend status (ACTIVE/ARCHIVED) to backend enum (Active/Archived)
    const backendStatus = data.status === 'ACTIVE' ? 'Active' : 'Archived';
    
    await updateAccount(clientId, {
      companyName: data.companyName,
      firstName: data.firstName,
      lastName: data.lastName,
      contactName: `${data.firstName} ${data.lastName}`, // Auto-generate contact name
      email: data.email,
      phone: data.phone || '',
      country: data.country,
      city: data.city || '',
      street: data.street || '',
      status: backendStatus as any, // Feature 019: Include status
      clientSegment: data.clientSegment, // Feature 019: Include client segment
    });

    // Refetch ticket data to show updated client info
    qc.invalidateQueries({ queryKey: ['ticket', id] });
  };

  const handleRemove = async () => {
    await unassociateMutation.mutateAsync({});
  };

  if (isLoading) return <div className="p-6 text-sm text-brand-text-secondary">Loading…</div>;
  if (!ticket) return <div className="p-6 text-sm text-red-500">Ticket not found.</div>;

  const ticketPriority = ticket.priority as TicketPriority;
  const ticketPriorityLabel = TICKET_PRIORITY_LABELS[ticketPriority];

  return (
    <div className="mx-auto max-w-[78rem] px-2 py-6 sm:px-4">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/tickets"
            title="Back to Tickets"
            aria-label="Back to Tickets"
            className="rounded-md border border-brand-border bg-brand-surface-card p-2 text-brand-text-secondary transition hover:bg-brand-surface"
          >
            <ChevronLeftIcon className="h-5 w-5" title="Back to Tickets" />
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-brand-text-primary">Ticket</h1>
            <span className="font-mono text-sm text-brand-text-secondary">{ticket.internalId}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {ticket.clientInfo && (
            <Link
              to={`/clients/${ticket.accountId}`}
              title="Open Account"
              aria-label="Open Account"
              className="rounded-full border border-brand-border bg-brand-surface-card p-3 text-brand-text-primary transition hover:bg-brand-surface"
            >
              <BuildingOffice2Icon className="h-5 w-5" title="Open Account" />
            </Link>
          )}
          <Link
            to={`/tickets/${ticket.ticketId}/edit`}
            title="Edit Ticket"
            aria-label="Edit Ticket"
            className="rounded-full bg-brand-filter-tab-active-bg p-3 text-brand-filter-tab-active-text transition hover:opacity-80"
          >
            <PencilSquareIcon className="h-5 w-5" title="Edit Ticket" />
          </Link>
        </div>
      </div>

      {/* Client Information - Feature 018: Editable client association */}
      <ClientInfoBox
        clientInfo={ticket.clientInfo}
        onAssociate={handleAssociate}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onRemove={handleRemove}
        isLoading={associateMutation.isPending || unassociateMutation.isPending || createClientMutation.isPending}
        className="mb-5"
      />

      {/* Subject and Description */}
      <div className="mb-4 rounded-xl border border-brand-border bg-white p-5 shadow-sm">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-text-secondary">Subject</h2>
        <p className="text-xl font-semibold text-brand-text-primary">{ticket.subject || 'No subject provided'}</p>
        <h3 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-brand-text-secondary">Description</h3>
        <p className="whitespace-pre-wrap text-sm text-brand-text-secondary">{ticket.description || 'No description provided.'}</p>
      </div>

      {/* Ticket overview */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <section className="xl:col-span-2">
          <div className="rounded-xl border border-brand-border bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-brand-text-primary">Ticket Overview</h2>
              <span className="text-xs text-brand-text-secondary">Ticket attributes</span>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 xl:grid-cols-3">
          <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
              <TagIcon className="h-4 w-4" title="Category" /> Category
            </div>
            <strong>{TICKET_CATEGORY_LABELS[ticket.category as TicketCategory]}</strong>
          </div>

          <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
              <IdentificationIcon className="h-4 w-4" title="Ticket ID" /> Ticket ID
            </div>
            <span className="font-mono text-xs">{ticket.internalId || '—'}</span>
          </div>

          <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
              <ExclamationTriangleIcon className="h-4 w-4" title="Priority" /> Priority
            </div>
            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getTicketPriorityPillClass(ticketPriority)}`}>
              {ticketPriorityLabel}
            </span>
          </div>

          <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
              <BuildingOffice2Icon className="h-4 w-4" title="Account" /> Account
            </div>
            {ticket.clientInfo ? (
              <div className="flex items-center gap-2">
                <Link to={`/clients/${ticket.accountId}`} className="text-brand-text-primary hover:underline">
                  {ticket.clientInfo.companyName || 'Open account'}
                </Link>
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-brand-text-secondary" title="Open account" />
              </div>
            ) : (
              <span className="text-brand-text-secondary">No account assigned</span>
            )}
          </div>

          <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
              <ClockIcon className="h-4 w-4" title="Created" /> Created
            </div>
            {new Date(ticket.createdAt).toLocaleString()}
          </div>

          <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
              <ClockIcon className="h-4 w-4" title="Updated" /> Updated
            </div>
            {new Date(ticket.updatedAt).toLocaleString()}
          </div>
            </div>
          </div>
        </section>

        <aside className="xl:col-span-1">
          <div className="rounded-xl border border-brand-border bg-white p-5 shadow-sm xl:sticky xl:top-4">
            <h2 className="mb-4 text-lg font-semibold text-brand-text-primary">Controls</h2>
            <div className="space-y-4">
              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <FlagIcon className="h-4 w-4" title="Status" /> Status
                </div>
                <StatusDropdown
                  currentStatus={ticket.status}
                  options={['Open', 'InProgress', 'WaitingOnClient', 'Resolved', 'Closed'] as const}
                  onUpdate={async (newStatus) => { await transition.mutateAsync(newStatus); }}
                  disabled={transition.isPending}
                  formatLabel={formatStatusLabel}
                  getStatusColor={getTicketStatusColor}
                />
              </div>

              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <UserCircleIcon className="h-4 w-4" title="Owner" /> Owner
                </div>
                <AssignOwner ticketId={ticket.ticketId} currentOwnerUserId={ticket.ownerUserId} />
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="xl:col-span-2 space-y-4">
          <TicketReplyComposer ticketId={ticket.ticketId} />

          <TicketRepliesThread
            ticketId={ticket.ticketId}
            lastReadAt={lastReadAt}
            contactName={ticket.clientInfo?.companyName ?? undefined}
          />
        </section>

        <aside className="xl:col-span-1">
          <InternalNotesPanel ticketId={ticket.ticketId} />
        </aside>
      </div>
    </div>
  );
}
